import { ComponentType, useCallback } from "react"
import { Box, SxProps } from "@mui/material"
import { SplideEventHandlers } from "@splidejs/react-splide"
import { Id } from "@turf/helpers"

import { Carousel, CarouselItem } from "components/Carousel"
import { VerticalCard } from "components/card/VerticalCard"
import { IExtendOptions } from "components/Carousel/Carousel"
import { Feature } from "geojson"

const DEFAULT_SLIDE_PER_PAGE = 3

interface IBaseCardProps<TData = Feature> {
  className?: string
  data: TData
}

export interface IFeatureCarouselLayoutProps<
  P extends object = {},
  TData extends Feature = Feature
> {
  sx?: SxProps
  features: Feature[]
  carouselOption?: IExtendOptions
  carouselSx?: SxProps
  onClickCard?: (id: Id) => void
  cardComponent?: ComponentType<IBaseCardProps<TData> & P>
  cardProps?: P
}

export const FeatureCarouselLayout = <
  P extends object = {},
  TData extends Feature = Feature
>({
  sx,
  features,
  carouselOption,
  carouselSx,
  onClickCard = () => {},
  cardComponent: CardComponent = VerticalCard,
  cardProps,
}: IFeatureCarouselLayoutProps<P, TData>) => {
  const carouselType =
    features && features.length < DEFAULT_SLIDE_PER_PAGE ? "slide" : "loop"

  const autoplay = features && features.length > DEFAULT_SLIDE_PER_PAGE

  const handleClickSlide: SplideEventHandlers["onClick"] = useCallback(
    (_splide, { slide }) => {
      onClickCard(slide.getAttribute("featureId"))
    },
    [onClickCard]
  )

  return (
    <Box sx={sx}>
      <Carousel
        onClick={handleClickSlide}
        options={{
          type: carouselType,
          pagination: true,
          focus: "center",
          trimSpace: false,
          autoplay,
          padding: 0,
          perPage: DEFAULT_SLIDE_PER_PAGE,
          drag: features.length > 1,
          ...carouselOption,
        }}
        sx={carouselSx}
      >
        {features.map((feature) => {
          const sponsoredId = feature?.properties?.sponsoredId
          const category = feature?.properties?.category

          return (
            <CarouselItem
              featureId={feature.id}
              key={`${sponsoredId || ""}${feature.id}`}
            >
              <CardComponent
                className={category}
                data={feature as TData}
                {...(cardProps ?? ({} as P))}
              />
            </CarouselItem>
          )
        })}
      </Carousel>
    </Box>
  )
}
